
import { supabase } from "@/integrations/supabase/client";

export interface SponsoredArticle {
  id: string;
  title: string;
  interviewee_name: string;
  sponsor_company: string;
  created_at: string;
  publication_status: string;
}

export interface SponsorMetrics {
  totalReaders: number;
  averageAttentionSeconds: number;
  averageTAV: number;
  totalKaiMessages: number;
  totalDebateArguments: number;
  totalPollAnswers: number;
  totalQuizCompletions: number;
}

export interface SponsorReport {
  sponsorCompany: string;
  articles: SponsoredArticle[];
  metrics: SponsorMetrics;
  dateRangeStart: string;
  dateRangeEnd: string;
}

/**
 * Fetch all sponsored (Betalt Brief) articles
 */
export async function getSponsoredArticles(): Promise<SponsoredArticle[]> {
  const { data, error } = await supabase
    .from('interviews')
    .select('id, title, interviewee_name, sponsor_company, created_at, publication_status')
    .eq('is_sponsored', true)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('[SponsorReportService] Error fetching sponsored articles:', error);
    return [];
  }

  return (data || []).map(d => ({
    id: d.id,
    title: d.title,
    interviewee_name: d.interviewee_name,
    sponsor_company: d.sponsor_company || 'Ukjent',
    created_at: d.created_at,
    publication_status: d.publication_status
  }));
}

/**
 * Fetch attention metrics for specific interview IDs
 */
export async function getSponsorMetrics(
  interviewIds: string[],
  startDate: Date,
  endDate: Date
): Promise<SponsorMetrics> {
  if (interviewIds.length === 0) {
    return {
      totalReaders: 0,
      averageAttentionSeconds: 0,
      averageTAV: 0,
      totalKaiMessages: 0,
      totalDebateArguments: 0,
      totalPollAnswers: 0,
      totalQuizCompletions: 0,
    };
  }

  const { data, error } = await supabase
    .from('attention_metrics')
    .select('*')
    .in('interview_id', interviewIds)
    .gte('created_at', startDate.toISOString())
    .lte('created_at', endDate.toISOString());

  if (error) {
    console.error('[SponsorReportService] Error fetching metrics:', error);
    return {
      totalReaders: 0,
      averageAttentionSeconds: 0,
      averageTAV: 0,
      totalKaiMessages: 0,
      totalDebateArguments: 0,
      totalPollAnswers: 0,
      totalQuizCompletions: 0,
    };
  }

  const metrics = data || [];
  const totalReaders = metrics.length;
  const totalPassive = metrics.reduce((sum, m) => sum + (m.passive_seconds || 0), 0);
  const totalTAV = metrics.reduce((sum, m) => sum + (m.total_attention_value || 0), 0);
  const totalKai = metrics.reduce((sum, m) => sum + (m.kai_messages || 0), 0);
  const totalDebate = metrics.reduce((sum, m) => sum + (m.debate_arguments || 0), 0);
  const totalPoll = metrics.filter(m => m.poll_answered).length;
  const totalQuiz = metrics.filter(m => m.quiz_completed).length;

  return {
    totalReaders,
    averageAttentionSeconds: totalReaders > 0 ? Math.round(totalPassive / totalReaders) : 0,
    averageTAV: totalReaders > 0 ? Math.round(totalTAV / totalReaders) : 0,
    totalKaiMessages: totalKai,
    totalDebateArguments: totalDebate,
    totalPollAnswers: totalPoll,
    totalQuizCompletions: totalQuiz,
  };
}

/**
 * Save a sponsor report to the database
 */
export async function saveSponsorReport(
  interviewId: string,
  sponsorCompany: string,
  startDate: Date,
  endDate: Date,
  metricsSnapshot: SponsorMetrics
): Promise<string | null> {
  const { data, error } = await supabase
    .from('sponsor_reports')
    .insert({
      interview_id: interviewId,
      sponsor_company: sponsorCompany,
      date_range_start: startDate.toISOString().split('T')[0],
      date_range_end: endDate.toISOString().split('T')[0],
      metrics_snapshot: metricsSnapshot as any,
    })
    .select('id')
    .single();

  if (error) {
    console.error('[SponsorReportService] Error saving report:', error);
    return null;
  }

  return data?.id || null;
}

/**
 * Get sponsored articles grouped by sponsor company
 */
export async function getSponsoredArticlesByCompany(): Promise<Record<string, SponsoredArticle[]>> {
  const articles = await getSponsoredArticles();
  const grouped: Record<string, SponsoredArticle[]> = {};
  
  for (const article of articles) {
    const company = article.sponsor_company || 'Ukjent';
    if (!grouped[company]) {
      grouped[company] = [];
    }
    grouped[company].push(article);
  }
  
  return grouped;
}
